import compact from 'lodash/compact';
import { TextField } from '@mui/material';
import CheckboxGroup from 'src/components/CheckboxGroup';
import MaskedInput from 'src/components/MaskedInput';
import RadioButtonGroup from 'src/components/RadioButtonGroup';
import ReadonlyField from 'src/components/ReadonlyField';
import Select from 'src/components/Select/Select';
import Switch from 'src/components/Switch';
import * as Yup from 'yup';
import { SOLUTION_SETTINGS } from '../constants';
import {
  SolutionAdlsSubscriptionItemField,
  SolutionCosmosSubscriptionItemField,
  SolutionFileSubscriptionItemField,
  SolutionSubscribeTo,
  SolutionSubscriptionFilterField,
  SolutionSubscriptionItemField,
  SolutionSubscriptionType,
  SolutionType,
  SubscriptionTarget,
} from '../types';
import {
  STORAGE_ACCOUNT_OPTIONS,
  SUBSCRIBE_FILTER_OPERATOR_OPTIONS,
  SUBSCRIBE_FILTER_CONDITION_OPTIONS,
  SUBSCRIPTION_SAS_PATTERN,
  ADLS_FORMAT_OPTIONS,
  SUBSCRIPTION_TARGET_OPTIONS,
} from './constants';
import SolutionSubscriptionEditFormRow from './components/SolutionSubscriptionEditFormRow';

export const getSubscriptionEditFormFields = (
  filters: any[],
  adlsFilters: any[],
  cosmosFilters: any[],
  useAuth?: boolean,
  subscriptionType?: string,
  isEditMsgSubscription?: boolean,
  storageEnabled?: boolean,
  solutionType?: string,
  subscribeToType?: string[],
  handlePaste?: any,
  handlePasteConnectionString?: any,
  handlePasteAuthorizationToken?: any,
  SUBSCIPTION_TYPE_OPTIONS?: any,
  INDUSTRIAL_SUBSCRIPTION_TYPE_OPTIONS?: any,
  isFileSubscriptionService?: boolean,
  isMessageSubscriptionService?: boolean,
  SUBSCRIBE_TO_OPTIONS?: any,
  SUBSCRIBE_TO_OPT_INDUSTRIAL?: any,
  isAlarmEventProcessing?: any,
  isTelemetryStreamProcessing?: any,
  handleConnectionStringPaste?: () => void,
  targetSelection?: string,
  targetOptions?: any
) => {
  const showServiceBusFields =
    isEditMsgSubscription ||
    targetSelection === SubscriptionTarget.ServiceBus;
  const showFilesFields =
    !isEditMsgSubscription &&
    targetSelection === SubscriptionTarget.Files;
  const showAdlsFields =
    !isEditMsgSubscription &&
    targetSelection === SubscriptionTarget.ADLS;
  const showCosmosFields =
    !isEditMsgSubscription &&
    targetSelection === SubscriptionTarget.Cosmos;

  return compact([
    // 1. Subscription type (create only, Messages only)
    !isEditMsgSubscription && {
      name: SolutionSubscriptionItemField.SubscriptionType,
      label: 'Subscription type',
      Component: RadioButtonGroup,
      value: SolutionSubscriptionType.Messages,
      defaultValue: SolutionSubscriptionType.Messages,
      required: true,
      options: [
        {
          value: SolutionSubscriptionType.Messages,
          label: 'Messages',
        },
      ],
    },

    // 2. Subscription Name (common for all targets)
    {
      name: SolutionSubscriptionItemField.Name,
      label: 'Subscription Name',
      Component: TextField,
      placeholder: 'Enter subscription name',
      required: true,
      validation: Yup.string().required('Subscription name is required'),
    },

    // 3. Target selection (create only)
    !isEditMsgSubscription && {
      name: SolutionSubscriptionItemField.TargetSelection,
      label: 'Target',
      Component: RadioButtonGroup,
      value: targetSelection,
      defaultValue: targetSelection,
      required: true,
      options: targetOptions || SUBSCRIPTION_TARGET_OPTIONS,
    },

    // 4. Subscribe to (always shown, options built in modal based on target)
    {
      name: SolutionSubscriptionItemField.Type,
      label: 'Subscribe to:',
      Component: CheckboxGroup,
      type: 'checkbox-group',
      validation: Yup.array().min(1, 'At least one selection is required'),
      required: true,
      labelStyle: { mr: 3 },
      showTooltip: true,
      options:
        solutionType === SolutionType.Industrial
          ? SUBSCRIBE_TO_OPT_INDUSTRIAL
          : SUBSCRIBE_TO_OPTIONS,
    },

    // --- Cosmos-specific fields ---
    showCosmosFields && {
      name: SolutionCosmosSubscriptionItemField.ConnectionString,
      label: 'Connection String',
      Component: MaskedInput,
      placeholder: 'Enter connection string',
      onClickPasteIcon: handleConnectionStringPaste,
      icon: true,
      required: true,
      validation: Yup.string().required('Connection String is required'),
    },
    showCosmosFields && {
      name: SolutionCosmosSubscriptionItemField.DatabaseName,
      label: 'Database Name',
      Component: TextField,
      placeholder: 'Enter name',
      required: true,
      validation: Yup.string().required('Database Name is required'),
    },
    showCosmosFields && {
      name: SolutionCosmosSubscriptionItemField.Container,
      label: 'Container',
      Component: TextField,
      placeholder: 'Enter container',
      required: true,
      validation: Yup.string().required('Container is required'),
    },
    showCosmosFields && {
      name: SolutionCosmosSubscriptionItemField.Partitionkey,
      label: 'Partition Key',
      Component: TextField,
      placeholder: 'Enter the partition key',
      required: true,
      validation: Yup.string().required('Partition key is required'),
    },

    // --- ADLS-specific fields ---
    showAdlsFields && {
      name: SolutionAdlsSubscriptionItemField.Format,
      label: 'Format',
      Component: Select,
      required: true,
      validation: Yup.string().required(
        'Solution Subscription Format is required'
      ),
      options: ADLS_FORMAT_OPTIONS,
    },
    showAdlsFields && {
      name: SolutionAdlsSubscriptionItemField.ContainerSaasURL,
      label: 'Container SAS URL',
      Component: MaskedInput,
      onClickPasteIcon: handlePaste,
      icon: true,
      placeholder: 'Enter Container SAS URL',
      required: true,
      validation: Yup.string().required('Container SAS URL is required'),
    },
    showAdlsFields && {
      name: SolutionAdlsSubscriptionItemField.FolderStructure,
      label: 'Destination Folder Format',
      Component: TextField,
      placeholder: 'Enter the format in 2023/06/12/24/34',
    },

    // --- Files-specific fields ---
    showFilesFields && {
      name: SolutionFileSubscriptionItemField.SourceStorageAccount,
      value: storageEnabled,
      label: 'Source Storage Account',
      Component: ReadonlyField,
      required: false,
      helperText: `Note: You need to enable source storage account to facilitate the file transfer to destination storage.
      <a href=${SOLUTION_SETTINGS}>Click here</a> to enable it.`,
    },
    showFilesFields && {
      name: SolutionFileSubscriptionItemField.DestinationType,
      label: 'Destination Storage Type',
      Component: Select,
      required: true,
      validation: Yup.string().required('Destination Storage Type is required'),
      options: STORAGE_ACCOUNT_OPTIONS,
      disabled: !storageEnabled,
    },
    showFilesFields && {
      name: SolutionFileSubscriptionItemField.FolderStructure,
      label: 'Destination Folder Format',
      Component: TextField,
      placeholder: 'Enter Destination Folder Format',
    },
    showFilesFields && {
      name: SolutionFileSubscriptionItemField.DestinationConnectionString,
      label: 'Connection String',
      Component: MaskedInput,
      placeholder: 'Enter Connection String',
      required: true,
      onClickPasteIcon: handlePasteConnectionString,
      icon: true,
      validation: Yup.string().required('Connection String is required'),
      disabled: !storageEnabled,
    },

    // --- Filter conditions ---
    // Messages/ServiceBus filter condition
    showServiceBusFields &&
      filters?.length > 1 &&
      (((solutionType === SolutionType.Consumer ||
        solutionType === SolutionType.ioTH) &&
        subscribeToType?.includes(SolutionSubscribeTo.Event)) ||
        (solutionType === SolutionType.Industrial &&
          subscribeToType?.includes(SolutionSubscribeTo.Telemetry))) && {
        name: SolutionSubscriptionItemField.FilterCondition,
        label: 'Apply if',
        placeholder: 'Select the value',
        Component: Select,
        options: SUBSCRIBE_FILTER_CONDITION_OPTIONS,
        initialValue: 'And',
        validation: Yup.string().required('Operator is required'),
        required: true,
        sx: { width: ({ spacing }) => spacing(30) },
      },

    // ADLS filter condition
    adlsFilters?.length > 1 &&
      showAdlsFields && {
        name: SolutionSubscriptionItemField.AdlsFilterCondition,
        label: 'Apply if',
        placeholder: 'Select the value',
        Component: Select,
        options: SUBSCRIBE_FILTER_CONDITION_OPTIONS,
        initialValue: 'And',
        validation: Yup.string().required('Operator is required'),
        required: true,
        sx: { width: ({ spacing }) => spacing(30) },
      },

    // Cosmos filter condition
    cosmosFilters?.length > 1 &&
      showCosmosFields && {
        name: SolutionSubscriptionItemField.CosmosFilterCondition,
        label: 'Apply if',
        placeholder: 'Select the value',
        Component: Select,
        options: SUBSCRIBE_FILTER_CONDITION_OPTIONS,
        initialValue: 'And',
        validation: Yup.string().required('Operator is required'),
        required: true,
        sx: { width: ({ spacing }) => spacing(30) },
      },

    // Messages/ServiceBus filter rows (Consumer/IoTH)
    showServiceBusFields &&
      (solutionType === SolutionType.Consumer ||
        solutionType === SolutionType.ioTH) &&
      subscribeToType?.includes(SolutionSubscribeTo.Event) && {
        name: SolutionSubscriptionItemField.Filters,
        appendLabel: 'Filter',
        childFields: getSubscriptionFilterFields(),
        RowComponent: SolutionSubscriptionEditFormRow,
      },

    // Messages/ServiceBus telemetry filter rows (Industrial)
    solutionType === SolutionType.Industrial &&
      showServiceBusFields &&
      subscribeToType?.includes(SolutionSubscribeTo.Telemetry) && {
        name: SolutionSubscriptionItemField.Filters,
        appendLabel: 'Telemetry Filter',
        childFields: getSubscriptionFilterFields(),
        RowComponent: SolutionSubscriptionEditFormRow,
        maxRows: 30,
      },

    // ADLS filter rows
    solutionType === SolutionType.Industrial &&
      showAdlsFields && {
        name: SolutionSubscriptionItemField.AdlsFilters,
        appendLabel: 'Filter',
        childFields: getSubscriptionFilterFields(),
        RowComponent: SolutionSubscriptionEditFormRow,
        maxRows: 30,
      },

    // Cosmos filter rows
    solutionType === SolutionType.Industrial &&
      showCosmosFields && {
        name: SolutionSubscriptionItemField.CosmosFilters,
        appendLabel: 'Filter',
        childFields: getSubscriptionFilterFields(),
        RowComponent: SolutionSubscriptionEditFormRow,
        maxRows: 30,
      },

    // --- Service Bus / Messages specific fields ---
    showServiceBusFields && {
      name: SolutionSubscriptionItemField.Target,
      label: 'Target URL',
      Component: TextField,
      placeholder: 'https://example.com',
      required: true,
      validation: Yup.string()
        .required('Target URL is required')
        .url('Target URL should have a correct format'),
    },
    showServiceBusFields && {
      name: SolutionSubscriptionItemField.Enabled,
      label: 'Enable',
      Component: Switch,
      type: 'checkbox',
    },
    showServiceBusFields && {
      name: SolutionSubscriptionItemField.Authorization,
      label: 'SAS Token',
      Component: MaskedInput,
      placeholder: 'Enter SAS Token',
      required: true,
      onClickPasteIcon: handlePasteAuthorizationToken,
      validation: Yup.string()
        .required('SAS Token is required')
        .matches(
          SUBSCRIPTION_SAS_PATTERN,
          'SAS Token should have "SharedAccessSignature sr={sr}&sig={sig}&se={ttl}&skn={saName}" format'
        ),
    },
  ]);
};

export const getSubscriptionFilterFields = () => [
  {
    name: SolutionSubscriptionFilterField.Property,
    label: 'Property Name',
    placeholder: 'Enter the name',
    Component: TextField,
    validation: Yup.string().required('Property name is required'),
    required: true,
    sx: { width: ({ spacing }) => spacing(20) },
  },
  {
    name: SolutionSubscriptionFilterField.OperatorValue,
    label: 'Operator',
    placeholder: 'Select the value',
    Component: Select,
    options: SUBSCRIBE_FILTER_OPERATOR_OPTIONS,
    validation: Yup.string().required('Operator is required'),
    required: true,
    sx: { width: ({ spacing }) => spacing(21) },
  },
  {
    name: SolutionSubscriptionFilterField.FilterValue,
    label: 'Value',
    placeholder: 'Enter the value',
    Component: TextField,
    validation: Yup.string().required('Value is required'),
    required: true,
    sx: { width: ({ spacing }) => spacing(35) },
  },
];
